import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'

import NoteAddIcon from '@mui/icons-material/NoteAdd'
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges'
import NotesIcon from '@mui/icons-material/Notes'
import AddCardIcon from '@mui/icons-material/AddCard'
import PauseCircleIcon from '@mui/icons-material/PauseCircle'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import SendIcon from '@mui/icons-material/Send'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'

import { sortByDateKey } from '../../util/sorting.js'
import { activityTypes } from '../../util/consts.js'
import { SmallBodyText } from '../styles/Text.js'
import {
  FlexRow,
  FlexColumn,
  WhiteBGContainer,
  CenteredFlexColumn,
  TimelineDot,
  TimelineConnector,
  TimelineItemContent
} from '../styles/Containers.js'
import ActivityLogItemContent from './ActivityLogItemContent.js'
import EditActivityButton from './EditActivityButton.js'

const Italics = styled.span`
  font-style: italic;
`

const iconMap = {
  NEW_REQUEST: <NoteAddIcon />,
  STATUS_CHANGE: <PublishedWithChangesIcon />,
  BILL_CREATED: <AddCardIcon />,
  BILL_DRAFT_CREATED: <AddCardIcon />,
  BILL_DRAFT_DELETED: <NotesIcon />,
  MANUAL: <NotesIcon />,
  DUE_DATE_PAUSED: <PauseCircleIcon />,
  DUE_DATE_UNPAUSED: <PlayCircleIcon />,
  RESPONSE_LINK_RESENT: <SendIcon />,
  LETTER_DRAFT_CREATED: <NoteAddIcon />,
  LETTER_DRAFT_EDITED: <NotesIcon />,
  LETTER_DRAFT_STATUS_CHANGE: <PublishedWithChangesIcon />,
  LETTER_DRAFT_DELETED: <NotesIcon />,
  LETTER_ASSIGNED_USER_UPDATED: <AssignmentIndIcon />,
  FOI_ASSIGNED_USER_UPDATED: <AssignmentIndIcon />,
  COMMUNICATION_DRAFT_CREATED: <NoteAddIcon />,
  COMMUNICATION_DRAFT_EDITED: <NotesIcon />,
  COMMUNICATION_SENT: <SendIcon />,
  COMMUNICATION_DRAFT_DELETED: <NotesIcon />
}

function getActivityIcon(type, name) {
  if (type === 'DUE_DATE_CHANGE') {
    if (name.startsWith('Due Date Paused - ') || name.startsWith('PAUSED_')) {
      return <PauseCircleIcon />
    }
    if (name.startsWith('Due Date Unpaused - ') || name.startsWith('UNPAUSED_')) {
      return <PlayCircleIcon />
    }
    return <PauseCircleIcon />
  }
  return iconMap[type]
}

function ActivityLogList({ activityList, users = [], onUpdateActivity = () => {} }) {
  const [sortedActivities, setSortedActivities] = useState()

  useEffect(() => {
    setSortedActivities([...activityList].sort(sortByDateKey('date')))
  }, [activityList])

  return (
    <FlexColumn gap="0">
      {sortedActivities &&
        sortedActivities.map((activity, i) => {
          return (
            <FlexRow gap="15px" key={activity.id}>
              <CenteredFlexColumn gap="0">
                <TimelineConnector connector={i !== 0} />
                <TimelineDot>{getActivityIcon(activity.type, activity.name)}</TimelineDot>
                <TimelineConnector connector={i !== sortedActivities.length - 1} />
              </CenteredFlexColumn>
              <TimelineItemContent data-testid="table-row">
                <WhiteBGContainer padding="8px 15px" width="100%">
                  <FlexColumn gap="3px">
                    <ActivityLogItemContent type={activity.type} name={activity.name} users={users} />
                    <SmallBodyText>
                      <Italics>{format(activity.date, 'MMM-dd-yyyy hh:mm a')}</Italics>
                    </SmallBodyText>
                  </FlexColumn>
                </WhiteBGContainer>
                {activity.type === 'MANUAL' && <EditActivityButton activity={activity} onSuccess={onUpdateActivity} />}
              </TimelineItemContent>
            </FlexRow>
          )
        })}
    </FlexColumn>
  )
}

ActivityLogList.propTypes = {
  activityList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(activityTypes),
      name: PropTypes.string.isRequired,
      date: PropTypes.instanceOf(Date).isRequired
    })
  ).isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      chipColor: PropTypes.string
    })
  ),
  onUpdateActivity: PropTypes.func
}

export default ActivityLogList
